import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, IdCard, Calendar, Mail, Save, Loader2, Trash2, AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [number, setNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setNumber(user.tesserino_number || "");
    setExpiry(user.tesserino_expiry || "");
    setEmail(user.tesserino_email || user.email || "");
  }, [user]);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API}/users/me/tesserino`, {
        tesserino_number: number,
        tesserino_expiry: expiry,
        tesserino_email: email,
      }, { withCredentials: true });
      toast.success(t("profile.saveSuccess"));
      checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("profile.saveError"));
    } finally { setSaving(false); }
  };

  const clearTesserino = async () => {
    if (!window.confirm(t("profile.clearConfirm"))) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/users/me/tesserino`, {
        tesserino_number: "",
        tesserino_expiry: "",
        tesserino_email: "",
      }, { withCredentials: true });
      setNumber(""); setExpiry(""); setEmail(user.email || "");
      toast.success(t("profile.clearSuccess"));
      checkAuth();
    } catch { toast.error(t("profile.clearError")); } finally { setSaving(false); }
  };

  // Compute days remaining
  let statusLabel = null;
  if (expiry) {
    try {
      const today = new Date();
      const exp = new Date(expiry);
      const days = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
      if (days < 0) {
        statusLabel = { tone: "danger", text: t("profile.statusExpiredDays", { count: Math.abs(days) }), icon: AlertTriangle };
      } else if (days <= 90) {
        statusLabel = { tone: "warn", text: t("profile.statusExpiringDays", { count: days }), icon: AlertTriangle };
      } else {
        statusLabel = { tone: "ok", text: t("profile.statusValidDays", { count: days }), icon: CheckCircle2 };
      }
    } catch {}
  }

  return (
    <div className="min-h-screen pb-24" data-testid="profile-page">
      <Header title={t("profile.headerTitle")} subtitle={user?.name || ""} right={
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg"
          style={{ border: "2px solid var(--border)" }} aria-label={t("profile.backAriaLabel")} data-testid="profile-back">
          <ArrowLeft size={18} />
        </button>
      } />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Account info */}
        <section className="vs-card p-5">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover"
                style={{ border: "2px solid var(--border)" }} referrerPolicy="no-referrer" />
            ) : (
              <div className="w-14 h-14 rounded-full flex items-center justify-center font-extrabold"
                style={{ background: "var(--primary)", color: "white" }}>
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="heading text-lg font-extrabold truncate">{user?.name}</div>
              <div className="text-sm truncate" style={{ color: "var(--text-muted)" }}>{user?.email}</div>
            </div>
          </div>
        </section>

        {/* Tesserino fitosanitario / Certiphyto */}
        <section className="vs-card p-5" data-testid="tesserino-section">
          <div className="flex items-start gap-3 mb-3">
            <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "var(--gradient-vine)" }}>
              <IdCard size={22} color="white" strokeWidth={2.4} />
            </div>
            <div>
              <h2 className="heading text-xl font-extrabold">{t("profile.sectionTitle")}</h2>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                {t("profile.sectionSubtitlePrefix")} <strong>{t("profile.sectionSubtitleBold")}</strong>{t("profile.sectionSubtitleSuffix")}
              </p>
            </div>
          </div>

          {statusLabel && (
            <div className="p-3 rounded-xl mb-3 inline-flex items-center gap-2"
              style={{
                background: statusLabel.tone === "danger" ? "#fde2e2" : statusLabel.tone === "warn" ? "var(--gold)" : "#e3f1e0",
                color: statusLabel.tone === "danger" ? "#7a0000" : statusLabel.tone === "warn" ? "#5a3500" : "#1e4923",
                border: `1.5px solid ${statusLabel.tone === "danger" ? "#a82020" : statusLabel.tone === "warn" ? "#7a4b00" : "#3d6e3d"}`,
              }}
              data-testid="tesserino-status">
              <statusLabel.icon size={16} />
              <span className="text-sm font-bold">{statusLabel.text}</span>
            </div>
          )}

          <form onSubmit={save} className="space-y-3">
            <div>
              <label className="vs-label">{t("profile.numberLabel")}</label>
              <div className="relative">
                <IdCard size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input className="vs-input" style={{ paddingLeft: 36 }}
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder={t("profile.numberPlaceholder")}
                  data-testid="tesserino-number" />
              </div>
            </div>
            <div>
              <label className="vs-label">{t("profile.expiryLabel")}</label>
              <div className="relative">
                <Calendar size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input className="vs-input" type="date" style={{ paddingLeft: 36 }}
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                  data-testid="tesserino-expiry" />
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                {t("profile.expiryHint")}
              </p>
            </div>
            <div>
              <label className="vs-label">{t("profile.emailLabel")}</label>
              <div className="relative">
                <Mail size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input className="vs-input" type="email" style={{ paddingLeft: 36 }}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={user?.email || t("profile.emailPlaceholder")}
                  data-testid="tesserino-email" />
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                {t("profile.emailHint")}
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              {(number || expiry) && (
                <button type="button" onClick={clearTesserino}
                  className="vs-btn-secondary inline-flex items-center gap-2"
                  style={{ color: "var(--accent)" }}
                  disabled={saving}
                  data-testid="tesserino-clear">
                  <Trash2 size={16} /> {t("profile.removeButton")}
                </button>
              )}
              <button type="submit" disabled={saving}
                className="vs-btn-primary flex-1 inline-flex items-center justify-center gap-2"
                style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
                data-testid="tesserino-save">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {saving ? t("profile.savingButton") : t("profile.saveButton")}
              </button>
            </div>
          </form>

          <div className="mt-4 p-3 rounded-xl text-xs"
            style={{ background: "var(--secondary)", border: "1.5px solid var(--border)", color: "var(--text-secondary)" }}>
            <strong>{t("profile.infoBold")}</strong> {t("profile.infoText")}
          </div>
        </section>

        {/* Cestino — Trash bin */}
        <section className="vs-card p-5" data-testid="cestino-section">
          <button
            type="button"
            onClick={() => navigate("/cestino")}
            className="w-full flex items-center gap-3 text-left"
            data-testid="cestino-link">
            <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "var(--gradient-vine)" }}>
              <Trash2 size={22} color="white" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="heading text-xl font-extrabold">{t("profile.trashTitle")}</h2>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                {t("profile.trashDescPrefix")} <strong>{t("profile.trashDescBold")}</strong>{t("profile.trashDescSuffix")}
              </p>
            </div>
            <ChevronRight size={22} style={{ color: "var(--text-muted)" }} />
          </button>
        </section>
      </main>
      <BottomNav />
    </div>
  );
}
